import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, LogOut, Save } from "lucide-react";

function HotelForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    brand: initial?.brand || "Booking.com",
    destination: initial?.destination || "new-york",
    destination_name: initial?.destination_name || "New York City",
    stars: initial?.stars || 4,
    editor_score: initial?.editor_score || 8.0,
    price_from: initial?.price_from || 199,
    image: initial?.image || "",
    tagline: initial?.tagline || "",
    description: initial?.description || "",
    amenities: (initial?.amenities || []).join(", "),
    deals: JSON.stringify(initial?.deals || [{ operator: "Booking.com", price: 199, url: "https://www.booking.com" }], null, 2),
    featured: initial?.featured || false,
  }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        stars: Number(form.stars),
        editor_score: Number(form.editor_score),
        price_from: Number(form.price_from),
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        deals: JSON.parse(form.deals || "[]"),
        gallery: initial?.gallery || [],
      };
      await onSave(payload);
      toast.success(initial ? "Hotel updated" : "Hotel created");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail ? String(err.response.data.detail) : "Save failed");
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="overline block mb-1">Name</label><Input data-testid="hf-name" value={form.name} onChange={set("name")} required /></div>
        <div><label className="overline block mb-1">Brand</label><Input data-testid="hf-brand" value={form.brand} onChange={set("brand")} required /></div>
        <div><label className="overline block mb-1">Destination key</label><Input data-testid="hf-dest" value={form.destination} onChange={set("destination")} /></div>
        <div><label className="overline block mb-1">Destination name</label><Input value={form.destination_name} onChange={set("destination_name")} /></div>
        <div><label className="overline block mb-1">Stars (1-5)</label><Input type="number" min={1} max={5} value={form.stars} onChange={set("stars")} /></div>
        <div><label className="overline block mb-1">Editor score</label><Input type="number" step="0.1" value={form.editor_score} onChange={set("editor_score")} /></div>
        <div><label className="overline block mb-1">Price from ($)</label><Input type="number" value={form.price_from} onChange={set("price_from")} /></div>
        <div><label className="overline block mb-1">Featured</label>
          <select className="w-full bg-white border border-charcoal/15 px-3 py-2 text-sm" value={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.value === "true" })}>
            <option value="false">No</option><option value="true">Yes</option>
          </select>
        </div>
      </div>
      <div><label className="overline block mb-1">Image URL</label><Input value={form.image} onChange={set("image")} placeholder="https://…" /></div>
      <div><label className="overline block mb-1">Tagline</label><Input value={form.tagline} onChange={set("tagline")} /></div>
      <div><label className="overline block mb-1">Description</label><Textarea rows={3} value={form.description} onChange={set("description")} /></div>
      <div><label className="overline block mb-1">Amenities (comma separated)</label><Input value={form.amenities} onChange={set("amenities")} /></div>
      <div>
        <label className="overline block mb-1">Deals (JSON array)</label>
        <Textarea rows={6} className="font-mono text-xs" value={form.deals} onChange={set("deals")} />
      </div>
      <DialogFooter>
        <button type="submit" disabled={saving} data-testid="hf-save" className="btn-primary">{saving ? "Saving…" : "Save hotel"}</button>
      </DialogFooter>
    </form>
  );
}

function ArticleForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    slug: initial?.slug || "",
    title: initial?.title || "",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    image: initial?.image || "",
    category: initial?.category || "Guide",
    author: initial?.author || "Choose My Hotel Editors",
    read_minutes: initial?.read_minutes || 5,
    featured: initial?.featured || false,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, read_minutes: Number(form.read_minutes) });
      toast.success(initial ? "Article updated" : "Article created");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail ? String(err.response.data.detail) : "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="overline block mb-1">Slug</label><Input data-testid="af-slug" value={form.slug} onChange={set("slug")} required /></div>
        <div><label className="overline block mb-1">Category</label><Input value={form.category} onChange={set("category")} /></div>
        <div><label className="overline block mb-1">Author</label><Input value={form.author} onChange={set("author")} /></div>
        <div><label className="overline block mb-1">Read minutes</label><Input type="number" value={form.read_minutes} onChange={set("read_minutes")} /></div>
        <div><label className="overline block mb-1">Featured</label>
          <select className="w-full bg-white border border-charcoal/15 px-3 py-2 text-sm" value={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.value === "true" })}>
            <option value="false">No</option><option value="true">Yes</option>
          </select>
        </div>
      </div>
      <div><label className="overline block mb-1">Title</label><Input data-testid="af-title" value={form.title} onChange={set("title")} required /></div>
      <div><label className="overline block mb-1">Image URL</label><Input value={form.image} onChange={set("image")} /></div>
      <div><label className="overline block mb-1">Excerpt</label><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} /></div>
      <div>
        <label className="overline block mb-1">Content</label>
        <RichTextEditor value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} placeholder="Write the article…" />
      </div>
      <DialogFooter>
        <button type="submit" disabled={saving} data-testid="af-save" className="btn-primary">{saving ? "Saving…" : "Save article"}</button>
      </DialogFooter>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({ hotels: 0, articles: 0, newsletter: 0, affiliate_clicks: 0 });
  const [affiliates, setAffiliates] = useState([]);
  const [clicksSummary, setClicksSummary] = useState({ by_operator: [], total: 0 });
  const [hotelDlg, setHotelDlg] = useState({ open: false, item: null });
  const [articleDlg, setArticleDlg] = useState({ open: false, item: null });

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [user, loading, nav]);

  const refresh = async () => {
    const [h, a, s, af, cs] = await Promise.all([
      api.get("/hotels"),
      api.get("/articles"),
      api.get("/admin/stats"),
      api.get("/admin/affiliates"),
      api.get("/admin/clicks/summary"),
    ]);
    setHotels(h.data); setArticles(a.data); setStats(s.data);
    setAffiliates(af.data); setClicksSummary(cs.data);
  };

  useEffect(() => { if (user) refresh(); }, [user]);

  if (loading) return <div className="min-h-screen bg-sand flex items-center justify-center text-charcoal/60">Loading…</div>;
  if (!user) return null;

  const saveHotel = async (payload) => {
    if (hotelDlg.item) await api.put(`/admin/hotels/${hotelDlg.item.id}`, payload);
    else await api.post("/admin/hotels", payload);
    await refresh();
  };
  const deleteHotel = async (id) => {
    if (!window.confirm("Delete this hotel?")) return;
    await api.delete(`/admin/hotels/${id}`);
    toast.success("Hotel deleted");
    await refresh();
  };
  const saveArticle = async (payload) => {
    if (articleDlg.item) await api.put(`/admin/articles/${articleDlg.item.id}`, payload);
    else await api.post("/admin/articles", payload);
    await refresh();
  };
  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Article deleted");
    await refresh();
  };

  return (
    <div className="min-h-screen bg-sand">
      <header className="border-b border-charcoal/10 bg-white">
        <div className="container-x flex items-center justify-between py-5">
          <div>
            <div className="overline">Choose My Hotel Admin</div>
            <div className="font-serif text-2xl">Welcome back, {user.name}</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-ghost">View site</Link>
            <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout" className="btn-ghost">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
          {[
            { k: "Hotels", v: stats.hotels },
            { k: "Articles", v: stats.articles },
            { k: "Subscribers", v: stats.newsletter },
            { k: "Affiliate clicks", v: stats.affiliate_clicks ?? 0 },
          ].map((s) => (
            <div key={s.k} className="bg-white border border-charcoal/10 p-6">
              <div className="overline">{s.k}</div>
              <div className="font-serif text-5xl mt-2">{s.v}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="hotels">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="hotels" data-testid="tab-hotels">Hotels</TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles">Articles</TabsTrigger>
            <TabsTrigger value="affiliates" data-testid="tab-affiliates">Affiliates</TabsTrigger>
          </TabsList>

          <TabsContent value="hotels" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-charcoal/70">{hotels.length} hotels</div>
              <Dialog open={hotelDlg.open} onOpenChange={(open) => setHotelDlg({ open, item: open ? hotelDlg.item : null })}>
                <DialogTrigger asChild>
                  <button data-testid="admin-add-hotel" onClick={() => setHotelDlg({ open: true, item: null })} className="btn-primary"><Plus size={14} /> Add hotel</button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">{hotelDlg.item ? "Edit hotel" : "New hotel"}</DialogTitle>
                    <DialogDescription>Fill the details and save. Deals accept JSON with operator/price/url/note.</DialogDescription>
                  </DialogHeader>
                  <HotelForm initial={hotelDlg.item} onSave={saveHotel} onClose={() => setHotelDlg({ open: false, item: null })} />
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white border border-charcoal/10 divide-y divide-charcoal/5" data-testid="admin-hotels-list">
              {hotels.map((h) => (
                <div key={h.id} className="flex items-center gap-5 p-4">
                  <img src={h.image} alt="" className="w-24 h-16 object-cover bg-stone" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-xl truncate">{h.name}</div>
                    <div className="text-xs text-charcoal/60">{h.destination_name} · {h.brand} · {h.stars}★ · ${h.price_from}</div>
                  </div>
                  <button onClick={() => setHotelDlg({ open: true, item: h })} data-testid={`edit-hotel-${h.id}`} className="p-2 hover:text-terracotta"><Pencil size={16} /></button>
                  <button onClick={() => deleteHotel(h.id)} data-testid={`delete-hotel-${h.id}`} className="p-2 hover:text-terracotta"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="articles" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-charcoal/70">{articles.length} articles</div>
              <Dialog open={articleDlg.open} onOpenChange={(open) => setArticleDlg({ open, item: open ? articleDlg.item : null })}>
                <DialogTrigger asChild>
                  <button data-testid="admin-add-article" onClick={() => setArticleDlg({ open: true, item: null })} className="btn-primary"><Plus size={14} /> Add article</button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">{articleDlg.item ? "Edit article" : "New article"}</DialogTitle>
                    <DialogDescription>Use double newlines for paragraphs. `## ` starts a section heading.</DialogDescription>
                  </DialogHeader>
                  <ArticleForm initial={articleDlg.item} onSave={saveArticle} onClose={() => setArticleDlg({ open: false, item: null })} />
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white border border-charcoal/10 divide-y divide-charcoal/5" data-testid="admin-articles-list">
              {articles.map((a) => (
                <div key={a.id} className="flex items-center gap-5 p-4">
                  <img src={a.image} alt="" className="w-24 h-16 object-cover bg-stone" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-xl truncate">{a.title}</div>
                    <div className="text-xs text-charcoal/60">{a.category} · {a.author} · {a.read_minutes} min</div>
                  </div>
                  <button onClick={() => setArticleDlg({ open: true, item: a })} data-testid={`edit-article-${a.id}`} className="p-2 hover:text-terracotta"><Pencil size={16} /></button>
                  <button onClick={() => deleteArticle(a.id)} data-testid={`delete-article-${a.id}`} className="p-2 hover:text-terracotta"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="affiliates" className="mt-6">
            <AffiliatesTab
              affiliates={affiliates}
              clicksSummary={clicksSummary}
              onSave={async (operator, payload) => {
                await api.put(`/admin/affiliates/${encodeURIComponent(operator)}`, payload);
                toast.success(`${operator} updated`);
                await refresh();
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AffiliatesTab({ affiliates, clicksSummary, onSave }) {
  const [editing, setEditing] = useState({});

  const setField = (op, field, value) => {
    setEditing((prev) => ({ ...prev, [op]: { ...(prev[op] ?? {}), [field]: value } }));
  };
  const valueOf = (cfg, field) => (editing[cfg.operator]?.[field] !== undefined ? editing[cfg.operator][field] : cfg[field]);
  const dirty = (cfg) => {
    const e = editing[cfg.operator];
    if (!e) return false;
    return Object.keys(e).some((k) => e[k] !== cfg[k]);
  };
  const save = async (cfg) => {
    const payload = {
      partner_id: valueOf(cfg, "partner_id") ?? "",
      template: valueOf(cfg, "template") ?? "{url}",
      enabled: valueOf(cfg, "enabled") ?? true,
    };
    await onSave(cfg.operator, payload);
    setEditing((prev) => ({ ...prev, [cfg.operator]: undefined }));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white border border-charcoal/10" data-testid="affiliates-list">
        <div className="p-5 border-b border-charcoal/10">
          <div className="overline">Operator templates</div>
          <p className="text-sm text-charcoal/65 mt-1">
            Placeholders: <code className="font-mono text-xs bg-cream px-1">{"{url}"}</code> (raw operator URL),{" "}
            <code className="font-mono text-xs bg-cream px-1">{"{hotel_id}"}</code>,{" "}
            <code className="font-mono text-xs bg-cream px-1">{"{partner_id}"}</code>.
          </p>
        </div>
        <div className="divide-y divide-charcoal/5">
          {affiliates.map((cfg) => (
            <div key={cfg.operator} className="p-5 grid md:grid-cols-12 gap-4 items-start" data-testid={`affiliate-row-${cfg.operator}`}>
              <div className="md:col-span-3">
                <div className="font-serif text-xl leading-tight">{cfg.operator}</div>
                <div className="flex items-center gap-2 mt-3">
                  <Switch
                    data-testid={`affiliate-enabled-${cfg.operator}`}
                    checked={valueOf(cfg, "enabled")}
                    onCheckedChange={(v) => setField(cfg.operator, "enabled", v)}
                  />
                  <span className="text-xs text-charcoal/60">{valueOf(cfg, "enabled") ? "Active" : "Paused"}</span>
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="overline block mb-1">Partner ID</label>
                <Input
                  data-testid={`affiliate-partner-${cfg.operator}`}
                  value={valueOf(cfg, "partner_id") ?? ""}
                  onChange={(e) => setField(cfg.operator, "partner_id", e.target.value)}
                />
              </div>
              <div className="md:col-span-5">
                <label className="overline block mb-1">URL template</label>
                <Input
                  data-testid={`affiliate-template-${cfg.operator}`}
                  className="font-mono text-xs"
                  value={valueOf(cfg, "template") ?? "{url}"}
                  onChange={(e) => setField(cfg.operator, "template", e.target.value)}
                />
              </div>
              <div className="md:col-span-1 flex md:justify-end">
                <button
                  data-testid={`affiliate-save-${cfg.operator}`}
                  onClick={() => save(cfg)}
                  disabled={!dirty(cfg)}
                  className="p-2.5 border border-charcoal/15 hover:bg-charcoal hover:text-sand disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Save"
                >
                  <Save size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-charcoal/10 p-6">
        <div className="overline">Click performance</div>
        <div className="font-serif text-5xl mt-2">{clicksSummary.total}</div>
        <div className="text-sm text-charcoal/60 mt-1">total clicks tracked</div>
        <div className="mt-6 space-y-3">
          {clicksSummary.by_operator.length === 0 && (
            <div className="text-sm text-charcoal/50 italic">No clicks yet — they'll appear here once visitors use the "View" buttons on hotel detail pages.</div>
          )}
          {clicksSummary.by_operator.map((row) => {
            const pct = clicksSummary.total ? Math.round((row.clicks / clicksSummary.total) * 100) : 0;
            return (
              <div key={row.operator}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{row.operator}</span>
                  <span className="font-mono">{row.clicks}</span>
                </div>
                <div className="h-1 bg-charcoal/8 relative">
                  <div className="absolute left-0 top-0 h-full bg-terracotta" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
